import React, { useState } from 'react'

/**
 * LawyerResults — Vista profesional para abogados previsionales
 *
 * Muestra veredicto, diferencia a recuperar, vías legales, hallazgos
 * y próximo paso. Diseño oscuro tipo dashboard profesional.
 *
 * Props: retorno completo de useLawyerData()
 */
export default function LawyerResults({
  veredicto,
  haberActual,
  haberReajustado,
  diferenciaMensual,
  incrementoPct,
  retroactivoEstimado,
  gapsPre94,
  gapsPost94,
  viasLegales,
  hallazgos,
  proximoPaso,
}) {
  const [tecnicoOpen, setTecnicoOpen] = useState(false)
  const fmt = v => (typeof v === 'number' ? Math.round(v).toLocaleString('es-AR') : '0')

  const verdictConfig = {
    viable: {
      icon: '✅',
      bg: 'bg-gradient-to-br from-green-950/60 to-gray-900 border-green-700/30',
      titleColor: 'text-green-400',
    },
    revision: {
      icon: '⚠️',
      bg: 'bg-gradient-to-br from-amber-950/60 to-gray-900 border-amber-600/30',
      titleColor: 'text-amber-400',
    },
    noviable: {
      icon: '✕',
      bg: 'bg-gradient-to-br from-red-950/60 to-gray-900 border-red-600/30',
      titleColor: 'text-red-400',
    },
  }

  const vc = verdictConfig[veredicto?.tipo] || verdictConfig.revision

  const impactoConfig = {
    alto: { label: 'Alto impacto', classes: 'bg-green-900/30 text-green-400 border border-green-700/40' },
    medio: { label: 'Impacto medio', classes: 'bg-amber-900/30 text-amber-400 border border-amber-600/40' },
    bajo: { label: 'Impacto bajo / Complementario', classes: 'bg-red-900/30 text-red-400 border border-red-600/40' },
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* ====== VEREDICTO ====== */}
      <div className={`rounded-2xl p-6 md:p-7 mb-6 flex items-start gap-5 border ${vc.bg}`}>
        <span className="text-3xl flex-shrink-0 mt-1">{vc.icon}</span>
        <div className="flex-1 min-w-0">
          <h2 className={`text-xl font-bold mb-1 ${vc.titleColor}`}>{veredicto?.titulo || 'Sin veredicto'}</h2>
          <p className="text-sm text-gray-400 leading-relaxed">{veredicto?.descripcion || ''}</p>
          {veredicto?.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {veredicto.tags.map((tag, i) => {
                const tc = {
                  green: 'bg-green-900/20 text-green-400 border-green-700/30',
                  gold: 'bg-amber-900/20 text-amber-400 border-amber-600/30',
                  blue: 'bg-blue-900/20 text-blue-400 border-blue-700/30',
                  red: 'bg-red-900/20 text-red-400 border-red-600/30',
                }[tag.tipo] || 'bg-gray-800 text-gray-400 border-gray-700'
                return (
                  <span key={i} className={`text-xs font-medium px-3 py-1.5 rounded-full border ${tc}`}>
                    {tag.texto}
                  </span>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* ====== LA DIFERENCIA A RECUPERAR ====== */}
      <SectionHead num="1" title="La diferencia a recuperar" />
      <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Haber actual (lo que cobra hoy)</div>
            <div className="text-2xl font-extrabold text-amber-400">${fmt(haberActual)}</div>
            <div className="text-xs text-gray-500 mt-1">Jubilación actual según ANSES</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Haber reajustado estimado</div>
            <div className="text-2xl font-extrabold text-green-400">${fmt(haberReajustado)}</div>
            <div className="text-xs text-gray-500 mt-1">Con índices y leyes aplicables</div>
          </div>
          <div className="sm:col-span-2 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg p-5 border border-green-700/30">
            <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Diferencia mensual a recuperar</div>
            <div className="text-3xl font-extrabold text-green-400">
              +${fmt(diferenciaMensual)} <span className="text-sm font-normal text-gray-400">por mes</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Retroactivo estimado: ${fmt(retroactivoEstimado)} (aprox. 12 meses de diferencia)
              {incrementoPct > 0 && <span className="ml-2 text-green-400">+{incrementoPct}%</span>}
            </div>
          </div>
        </div>
      </div>

      {/* ====== VÍAS LEGALES ====== */}
      <SectionHead num="2" title="Vías legales disponibles" />
      <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 mb-4">
        <p className="text-sm text-gray-400 px-5 pt-5 pb-2">
          Estas son las herramientas legales disponibles para este caso. Cada una tiene un impacto estimado y un nivel de complejidad.
        </p>
      </div>

      {viasLegales.length === 0 ? (
        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50 mb-6">
          <p className="text-sm text-gray-500">No se detectaron vías legales aplicables para este caso.</p>
        </div>
      ) : (
        viasLegales.map((via) => {
          const ic = impactoConfig[via.impacto] || impactoConfig.bajo
          return (
            <div key={via.id} className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50 mb-3 hover:border-gray-600 transition-colors">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${ic.classes}`}>
                  {ic.label}
                </span>
                <h4 className="text-sm font-semibold text-gray-200 flex-1">{via.titulo}</h4>
                {via.impactoEstimado && (
                  <span className="text-sm font-bold text-green-400">{via.impactoEstimado}</span>
                )}
              </div>
              <div className="text-sm text-gray-400 leading-relaxed">
                {via.descripcion}
                {via.fundamentos && (
                  <div className="mt-2 p-2.5 bg-gray-800 rounded-md text-xs text-gray-500">
                    📋 Fundamentos: {via.fundamentos}
                  </div>
                )}
                {via.observacion && (
                  <div className="mt-2 p-2.5 bg-blue-950/30 rounded-md text-xs text-blue-400 border border-blue-800/30">
                    {via.observacion}
                  </div>
                )}
              </div>
            </div>
          )
        })
      )}

      {/* ====== HALLAZGOS ====== */}
      <SectionHead num="3" title="Hallazgos en la liquidación actual" />
      <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 mb-6 overflow-hidden">
        {hallazgos.length === 0 ? (
          <div className="p-5">
            <p className="text-sm text-gray-500">No se detectaron hallazgos relevantes.</p>
          </div>
        ) : (
          hallazgos.map((h, i) => (
            <div key={i} className="flex items-start gap-3 px-5 py-4 border-b border-gray-700/30 last:border-0">
              <span className="text-base flex-shrink-0 mt-0.5">{h.icon}</span>
              <div className="text-sm text-gray-300">
                <strong className="font-semibold">{h.titulo}</strong>
                {h.detalle && <span className="block text-xs text-gray-500 mt-0.5">{h.detalle}</span>}
              </div>
            </div>
          ))
        )}
      </div>

      {/* ====== DETALLE TÉCNICO (acordeón) ====== */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden mb-6">
        <button
          onClick={() => setTecnicoOpen(!tecnicoOpen)}
          className="w-full flex items-center justify-between px-5 py-4 text-sm font-medium text-gray-400 hover:text-gray-300 transition-colors cursor-pointer"
        >
          <h3>📊 Ver detalle técnico del cálculo</h3>
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${tecnicoOpen ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {tecnicoOpen && (
          <div className="px-5 pb-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              <Metric label="Fórmula aplicada" value="GENERAL" />
              <Metric label="Hab. actual" value={`$${fmt(haberActual)}`} />
              <Metric label="Hab. reajustado" value={`$${fmt(haberReajustado)}`} />
              <Metric label="Diferencia" value={`+$${fmt(diferenciaMensual)}`} className="text-green-400" />
              <Metric label="Retroactivo est." value={`$${fmt(retroactivoEstimado)}`} />
              <Metric label="Gaps totales" value={gapsPre94 + gapsPost94} />
              <Metric label="Pre-94 sin dato" value={gapsPre94} />
              <Metric label="Post-94 sin dato" value={gapsPost94} />
              <Metric label="Incremento" value={incrementoPct > 0 ? `+${incrementoPct}%` : '-'} className={incrementoPct > 0 ? 'text-green-400' : ''} />
              <Metric label="Vías legales" value={viasLegales.length} />
            </div>
          </div>
        )}
      </div>

      {/* ====== PRÓXIMO PASO ====== */}
      {proximoPaso?.titulo && (
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-5 border border-gray-700/50 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between mt-2">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center text-lg flex-shrink-0">
              🎯
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-200">{proximoPaso.titulo}</h3>
              <p className="text-xs text-gray-400 mt-0.5">{proximoPaso.descripcion}</p>
            </div>
          </div>
          <button
            type="button"
            className="bg-blue-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-500 transition-colors flex-shrink-0"
          >
            Ver pasos siguientes →
          </button>
        </div>
      )}

      {/* spacer */}
      <div className="h-8" />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Subcomponentes                                                      */
/* ------------------------------------------------------------------ */

function SectionHead({ num, title }) {
  return (
    <div className="flex items-center gap-2.5 mb-3.5 mt-2">
      <span className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold text-blue-400">
        {num}
      </span>
      <h3 className="text-sm font-semibold text-gray-300">{title}</h3>
    </div>
  )
}

function Metric({ label, value, className = 'text-gray-200' }) {
  return (
    <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
      <div className="text-[11px] text-gray-500">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 ${className}`}>{value}</div>
    </div>
  )
}
